package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.util.spreadsheet.SpreadsheetFormatException
import br.com.vvrefeicoes.portal.core.util.spreadsheet.pickSpreadsheetGrid
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhDepartmentUnit
import br.com.vvrefeicoes.portal.domain.model.RhSheetColumn
import br.com.vvrefeicoes.portal.domain.model.RhSheetData
import br.com.vvrefeicoes.portal.domain.model.RhSheetKind
import br.com.vvrefeicoes.portal.domain.model.RhUnitOption
import br.com.vvrefeicoes.portal.domain.model.departmentUnits
import br.com.vvrefeicoes.portal.domain.model.gridToRhSheet
import br.com.vvrefeicoes.portal.domain.repository.RhSheetRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.app_load_error
import portalvv.shared.generated.resources.rh_delete_error
import portalvv.shared.generated.resources.rh_import_error
import portalvv.shared.generated.resources.rh_import_wrong_sheet

class RhSheetController(
    private val repository: RhSheetRepository,
    private val kind: RhSheetKind,
    private val columns: List<RhSheetColumn>,
    private val scope: CoroutineScope,
    /** Ajusta a grade lida do arquivo ao formato tabular (cabeçalho na primeira linha) antes de casar as colunas. */
    private val normalizeGrid: (List<List<String>>) -> List<List<String>> = { it },
) {
    var period by mutableStateOf(MonthPeriod(2000, 1))
        private set

    var data by mutableStateOf<RhSheetData?>(null)
        private set

    var draft by mutableStateOf<RhSheetData?>(null)
        private set

    var editing by mutableStateOf(false)
        private set

    var importing by mutableStateOf(false)
        private set

    var replaceConfirmOpen by mutableStateOf(false)
        private set

    var deleteConfirmOpen by mutableStateOf(false)
        private set

    var deleting by mutableStateOf(false)
        private set

    /** Conferência de unidades aberta entre a leitura da planilha e a gravação. */
    var unitReview by mutableStateOf<RhUnitReview?>(null)
        private set

    var savingUnits by mutableStateOf(false)
        private set

    var error by mutableStateOf<StringResource?>(null)
        private set

    /** Quem fez a última importação/edição da planilha e quando (ISO). */
    var updatedBy by mutableStateOf("")
        private set

    var updatedAt by mutableStateOf("")
        private set

    private var pendingGrid: List<List<String>>? = null
    private var started = false

    fun applyPeriod(newPeriod: MonthPeriod) {
        if (started && period == newPeriod) return
        started = true
        period = newPeriod
        scope.launch { load() }
    }

    fun requestImport() {
        if (importing) return
        scope.launch {
            importing = true
            try {
                val grid = pickSpreadsheetGrid() ?: return@launch
                if (data != null) {
                    pendingGrid = grid
                    replaceConfirmOpen = true
                } else {
                    applyGrid(grid)
                }
            } catch (exception: SpreadsheetFormatException) {
                error = Res.string.rh_import_wrong_sheet
            } catch (throwable: Throwable) {
                error = Res.string.rh_import_error
            } finally {
                importing = false
            }
        }
    }

    fun confirmReplace() {
        replaceConfirmOpen = false
        val grid = pendingGrid ?: return
        pendingGrid = null
        scope.launch {
            try {
                applyGrid(grid)
            } catch (exception: SpreadsheetFormatException) {
                error = Res.string.rh_import_wrong_sheet
            } catch (throwable: Throwable) {
                error = Res.string.rh_import_error
            }
        }
    }

    fun dismissReplace() {
        replaceConfirmOpen = false
        pendingGrid = null
    }

    fun selectUnit(
        department: String,
        unitId: String?,
    ) {
        val review = unitReview ?: return
        unitReview =
            review.copy(
                departments = review.departments.map { if (it.department == department) it.copy(unitId = unitId) else it },
            )
    }

    fun dismissUnitReview() {
        if (!savingUnits) unitReview = null
    }

    fun confirmUnitReview() {
        val review = unitReview ?: return
        if (savingUnits) return
        scope.launch {
            savingUnits = true
            try {
                persist(review.sheet, review.departments.associate { it.department to it.unitId })
                unitReview = null
            } catch (throwable: Throwable) {
                error = Res.string.rh_import_error
            } finally {
                savingUnits = false
            }
        }
    }

    fun requestDelete() {
        if (data == null || deleting) return
        deleteConfirmOpen = true
    }

    fun dismissDelete() {
        deleteConfirmOpen = false
    }

    /** Exclui do servidor todos os dados da aba no período selecionado. */
    fun confirmDelete() {
        deleteConfirmOpen = false
        if (deleting) return
        val target = period
        scope.launch {
            deleting = true
            try {
                repository.delete(kind, target)
                if (period == target) {
                    data = null
                    draft = null
                    editing = false
                }
            } catch (throwable: Throwable) {
                error = Res.string.rh_delete_error
            }
            deleting = false
        }
    }

    fun enterEdit() {
        if (data == null) return
        draft = data
        editing = true
    }

    fun cancelEdit() {
        draft = null
        editing = false
    }

    fun updateCell(
        rowIndex: Int,
        columnIndex: Int,
        value: String,
    ) {
        val current = draft ?: return
        if (rowIndex !in current.rows.indices) return
        val row = current.rows[rowIndex].toMutableList()
        if (columnIndex !in row.indices) return
        row[columnIndex] = value
        val rows = current.rows.toMutableList().apply { this[rowIndex] = row }
        draft = current.copy(rows = rows)
    }

    fun save() {
        val current = draft ?: return
        scope.launch {
            try {
                repository.save(kind, current.period, current.rows.toKeyed(columns))
                data = current
                draft = null
                editing = false
            } catch (throwable: Throwable) {
                error = Res.string.app_load_error
            }
        }
    }

    fun dismissError() {
        error = null
    }

    private suspend fun load() {
        try {
            val loaded = repository.load(kind, period)
            data =
                loaded?.let { sheet ->
                    RhSheetData(
                        period = period,
                        rows = sheet.rows.toAligned(columns),
                        costCenterIds = sheet.rows.map { it["costCenterId"]?.takeIf(String::isNotBlank) },
                    )
                }
            updatedBy = loaded?.updatedBy.orEmpty()
            updatedAt = loaded?.updatedAt.orEmpty()
        } catch (throwable: Throwable) {
            error = Res.string.app_load_error
        }
        draft = null
        editing = false
    }

    private suspend fun applyGrid(grid: List<List<String>>) {
        val parsed = gridToRhSheet(normalizeGrid(grid), period, columns)
        // Uma planilha de outra aba pode ter uma ou outra coluna de mesmo nome: sem as colunas
        // obrigatórias preenchidas ela é recusada, em vez de gravar um mês quase todo vazio.
        val missingRequired =
            columns.withIndex().any { (index, column) ->
                column.required && parsed.rows.none { row -> row.getOrElse(index) { "" }.isNotBlank() }
            }
        if (missingRequired) throw SpreadsheetFormatException("A planilha não tem as colunas esperadas para esta aba")
        val departmentIndex = columns.indexOfFirst { it.header == DEPARTMENT_HEADER }
        val departments = parsed.rows.map { row -> row.getOrElse(departmentIndex) { "" }.trim() }.filter { it.isNotEmpty() }
        if (departments.isEmpty()) {
            persist(parsed, emptyMap())
            return
        }
        val preview = repository.previewUnits(departments.distinct())
        unitReview = RhUnitReview(parsed, preview.units, departmentUnits(departments, preview))
    }

    /** Grava a planilha e recarrega do servidor, que devolve as linhas já com a unidade conferida. */
    private suspend fun persist(
        sheet: RhSheetData,
        unitByDepartment: Map<String, String?>,
    ) {
        repository.save(kind, sheet.period, sheet.rows.toKeyed(columns), unitByDepartment)
        if (period == sheet.period) {
            data = sheet
            load()
        }
    }
}

/** Planilha lida e ainda não gravada, com a unidade proposta para cada departamento. */
data class RhUnitReview(
    val sheet: RhSheetData,
    val units: List<RhUnitOption>,
    val departments: List<RhDepartmentUnit>,
)

private const val DEPARTMENT_HEADER = "departamento"

private fun List<List<String>>.toKeyed(columns: List<RhSheetColumn>): List<Map<String, String>> = map { row -> columns.mapIndexed { index, column -> column.header to row.getOrElse(index) { "" } }.toMap() }

private fun List<Map<String, String>>.toAligned(columns: List<RhSheetColumn>): List<List<String>> = map { row -> columns.map { column -> row[column.header].orEmpty() } }

@Composable
fun rememberRhSheetController(
    repository: RhSheetRepository,
    kind: RhSheetKind,
    columns: List<RhSheetColumn>,
    normalizeGrid: (List<List<String>>) -> List<List<String>> = { it },
): RhSheetController {
    val scope = rememberCoroutineScope()
    return remember(repository, kind, columns) { RhSheetController(repository, kind, columns, scope, normalizeGrid) }
}
